import { MessageFilled } from "@ant-design/icons";
import { Empty, Flex } from "antd";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getContractListThunk } from "tm-shared/api";
import {
    Contract,
    Point,
    TaskDMP as TaskDMPType,
    TaskRoute,
    TaskStatus,
} from "tm-shared/types";
import { formatUAHStandard } from "tm-shared/utils";

import { GalleryUploader } from "@/domains/media";
import { getPointContractSourceIds, usePointDistance } from "@/domains/point";
import {
    CommentModal,
    StatusCycleButton,
    TaskInfoModal,
    useActiveScrollNavigator,
    useCheckCommentOnReject,
    useStickyFilteredItems,
    useTaskCompletion,
    useTaskConfigSelector,
} from "@/domains/tasks";
import { SVScanButton } from "@/features/SupervisorScan";
import {
    useAppDispatch,
    useAppSelector,
    useTaskPayloadSelector,
} from "@/hooks/redux";
import { Header } from "@/shared/layout";
import { BottomSheet } from "@/shared/ui/BottomSheet";
import { OutlineIconButton } from "@/shared/ui/OutlineIconButton";
import { PrimaryText } from "@/shared/ui/Typography";
import { mutateDMPPhotos, patchDMPEntity } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";
import { buildPhotoCaptions } from "@/utils/photo/captions";

import { DMPController } from "./components/DMPController";
import { TaskDMP } from "./components/TaskDMP";
import { TaskFinish } from "./components/TaskFinish";
import {
    StyledBottomSheetTop,
    StyledList,
    StyledMainContainer,
} from "./Styled";

interface DMPPageProps {
    point: Point;
    task: TaskDMPType;
}

export const DMPPage: FC<DMPPageProps> = ({ task, point }) => {
    const dispatch = useAppDispatch();
    const [modal, setModal] = useState<"comment" | "taskInfo" | null>(null);

    const pointId = point.id;
    const taskId = task.id;

    const { firstName, lastName } = useAppSelector((s) => ({
        firstName: s.user.firstName,
        lastName: s.user.lastName,
    }));

    const targetsContracts = useAppSelector(
        (state) => state.offline.targetsContracts,
    );

    const allContracts = useMemo(() => {
        if (!targetsContracts) return [];

        const contractSources = getPointContractSourceIds(point);

        const contracts: Contract[] = [];
        const seenIds = new Set<string>();

        contractSources.forEach((sourceId) => {
            const sourceContracts = targetsContracts[sourceId!];
            if (sourceContracts) {
                Object.values(sourceContracts).forEach((contract) => {
                    if (contract && !seenIds.has(contract.id)) {
                        contracts.push(contract);
                        seenIds.add(contract.id);
                    }
                });
            }
        });

        return contracts;
    }, [targetsContracts, point]);

    const dmpPayloadMap = useTaskPayloadSelector(TaskRoute.Dmp, taskId);

    // const dmpPayloadMap = useMemo(() => dmp[taskId] ?? {}, [dmp, taskId]);
    const { activeItem = null, showUncompleted } = useTaskConfigSelector({
        pointId,
        taskId,
    });

    const dmpSubtasks = task.context.dmp;

    const prevDMPIdRef = useRef<string | null>(null);

    const onCloseBottomSheet = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                pointId,
                taskId,
                value: null,
            }),
        );
    }, [dispatch, pointId, taskId]);

    const onChangeActive = useCallback(
        (id: string) => {
            dispatch(
                setMdTasksConfig({
                    pointId,
                    taskId,
                    value: id,
                }),
            );
        },
        [dispatch, pointId, taskId],
    );

    const { validateAndNotify } = useCheckCommentOnReject();

    const handleCheckCommentOnReject = useCallback(
        (nextId: string | null) => {
            const currentId = prevDMPIdRef.current ?? "";
            const currentPayload = dmpPayloadMap?.[currentId];

            if (validateAndNotify(currentPayload) || !nextId) {
                return;
            }

            onChangeActive(nextId);
        },
        [dmpPayloadMap, onChangeActive, validateAndNotify],
    );

    const { filteredItems: filteredTasks } = useStickyFilteredItems({
        items: dmpSubtasks,
        showUncompleted: showUncompleted ?? false,
        getItemId: (dmp) => dmp.subtaskId,
        payloadsMap: dmpPayloadMap ?? {},
        isIncomplete: (payload) => {
            return !payload || payload.status === TaskStatus.Incomplete;
        },
    });

    const { activeIndex, getItemRef, onNext, onPrev, scrollToActive } =
        useActiveScrollNavigator({
            ids: filteredTasks.map((t) => t.subtaskId),
            activeId: activeItem,
            onChangeActive: handleCheckCommentOnReject,
        });

    const activeTaskName = task?.context?.dmp?.[activeIndex]?.name;
    const activeTaskContractId =
        task?.context?.dmp?.[activeIndex]?.contractId ?? "";

    const activePayload = useMemo(
        () =>
            activeItem
                ? {
                      id: activeItem,
                      payload: dmpPayloadMap?.[activeItem],
                      status:
                          dmpPayloadMap?.[activeItem]?.status ??
                          TaskStatus.Incomplete,
                      name: activeTaskName,
                      contractId: activeTaskContractId,
                  }
                : null,
        [activeItem, activeTaskContractId, activeTaskName, dmpPayloadMap],
    );

    const currentContract = useMemo(
        () =>
            allContracts.find(
                (contract) => contract.id === activeTaskContractId,
            ),
        [activeTaskContractId, allContracts],
    );

    const onClickInfoButton = () => {
        setModal("taskInfo");
    };

    const resetStatusIfNeeded = useCallback(
        (dmpId: string) => {
            const payload = dmpPayloadMap?.[dmpId];

            if (!payload) return;

            if (payload.status === TaskStatus.Rejected && !payload.comment) {
                dispatch(
                    patchDMPEntity({
                        taskId,
                        contractId: activeTaskContractId,
                        dmpId,
                        patch: { status: TaskStatus.Incomplete },
                    }),
                );
            }
        },
        [dmpPayloadMap, dispatch, taskId, activeTaskContractId],
    );

    const onChangeTaskStatus = useCallback(
        (value: TaskStatus, id?: string) => {
            const targetId = id ?? activePayload?.id;

            if (!targetId) return;

            dispatch(
                patchDMPEntity({
                    taskId,
                    contractId: activePayload?.contractId ?? "",
                    dmpId: targetId,
                    patch: { status: value, comment: "" },
                }),
            );
        },
        [activePayload, dispatch, taskId],
    );

    const onUploadPhoto = useCallback(
        (photo: string) => {
            if (!activePayload) return;

            dispatch(
                mutateDMPPhotos({
                    taskId,
                    contractId: activePayload.contractId,
                    dmpId: activePayload.id,
                    option: "add",
                    photo,
                }),
            );
        },
        [activePayload, dispatch, taskId],
    );

    const onDeletePhoto = useCallback(
        (photoId: string) => {
            if (!activePayload) return;

            dispatch(
                mutateDMPPhotos({
                    taskId,
                    dmpId: activePayload.id,
                    contractId: activePayload.contractId,
                    option: "remove",
                    photoId,
                }),
            );
        },
        [activePayload, dispatch, taskId],
    );

    const handlerMessage = useCallback(() => {
        if (!activePayload || activePayload.status === TaskStatus.Incomplete)
            return;

        setModal("comment");
    }, [activePayload]);

    const handleChangeComment = useCallback(
        (newComment: string) => {
            if (!activePayload) return;

            dispatch(
                patchDMPEntity({
                    taskId,
                    contractId: activePayload.contractId,
                    dmpId: activePayload.id,
                    patch: { comment: newComment },
                }),
            );
        },
        [activePayload, dispatch, taskId],
    );

    useEffect(() => {
        const prev = prevDMPIdRef.current;

        if (prev && activeItem !== prev) {
            resetStatusIfNeeded(prev);
        }

        prevDMPIdRef.current = activeItem ?? null;
    }, [activeItem, resetStatusIfNeeded]);

    const { isAllCompleted } = useTaskCompletion({
        payloadMap: dmpPayloadMap,
        expectedCount: dmpSubtasks.length,
    });

    const { distance } = usePointDistance({
        pointGeo: point.market.building.geo,
    });

    const captions = useMemo(
        () =>
            buildPhotoCaptions({
                pointName: point.name,
                taskName: task.name,
                userFullName: `${firstName} ${lastName}`,
                distance,
            }),
        [distance, firstName, lastName, point.name, task.name],
    );

    useEffect(() => {
        if (allContracts?.length) return;

        const loadContracts = async () => {
            try {
                await Promise.all([
                    dispatch(
                        getContractListThunk({
                            pointId,
                            isActive: true,
                        }),
                    ),
                ]);
            } catch (error) {
                console.error("Error loading contracts:", error);
            }
        };

        loadContracts();
    }, [allContracts?.length, dispatch, pointId]);

    useEffect(() => {
        scrollToActive();
    }, [isAllCompleted, scrollToActive]);

    return (
        <>
            <Header
                back
                title={task.name}
                rightActions={
                    <>
                        <SVScanButton pointId={pointId} taskId={taskId} />
                    </>
                }
            />

            <DMPController task={task} point={point} />

            <StyledMainContainer>
                <StyledList>
                    {filteredTasks.length > 0 ? (
                        filteredTasks.map((dmp) => {
                            const { subtaskId } = dmp;
                            const currentDMPPayload =
                                dmpPayloadMap?.[subtaskId];
                            const isActive = subtaskId === activeItem;

                            return (
                                <div
                                    key={subtaskId}
                                    ref={getItemRef(subtaskId)}
                                >
                                    <TaskDMP
                                        dmp={dmp}
                                        isActive={isActive}
                                        onChangeActive={onChangeActive}
                                        payload={currentDMPPayload}
                                        onChangeTaskStatus={onChangeTaskStatus}
                                    />
                                </div>
                            );
                        })
                    ) : (
                        <Empty
                            style={{ marginTop: 32 }}
                            description={"Задач не знайдено"}
                        />
                    )}
                </StyledList>

                <TaskFinish
                    isAllCompleted={isAllCompleted}
                    dmpPayloadMap={dmpPayloadMap}
                    pointId={pointId}
                    taskId={taskId}
                />
            </StyledMainContainer>

            <BottomSheet
                open={!!activeItem}
                onClose={onCloseBottomSheet}
                onNext={onNext}
                onPrev={onPrev}
                onClickInfoButton={onClickInfoButton}
            >
                <StyledBottomSheetTop>
                    <PrimaryText>{activePayload?.name}</PrimaryText>
                    <Flex gap="20px">
                        <OutlineIconButton
                            icon={<MessageFilled />}
                            onClick={handlerMessage}
                            disabled={
                                activePayload?.status === TaskStatus.Incomplete
                            }
                        />

                        <StatusCycleButton
                            value={
                                activePayload?.status ?? TaskStatus.Incomplete
                            }
                            onChange={onChangeTaskStatus}
                            states={[
                                TaskStatus.Incomplete,
                                TaskStatus.Rejected,
                            ]}
                            disabled={
                                activePayload?.status === TaskStatus.Completed
                            }
                        />
                    </Flex>
                </StyledBottomSheetTop>

                <GalleryUploader
                    previewTitle={activePayload?.name ?? ""}
                    onDelete={onDeletePhoto}
                    onUpload={onUploadPhoto}
                    activeItemId={activePayload?.id}
                    pointId={pointId}
                    savedPhotos={activePayload?.payload?.photos ?? []}
                    disabled={activePayload?.status === TaskStatus.Rejected}
                    captionLines={captions}
                />
            </BottomSheet>

            {modal === "comment" && activePayload && (
                <CommentModal
                    onCloseModal={() => setModal(null)}
                    onConfirm={handleChangeComment}
                    initialComment={activePayload.payload?.comment ?? ""}
                    taskStatus={activePayload.status}
                />
            )}

            {modal === "taskInfo" && (
                <TaskInfoModal
                    onCloseModal={() => setModal(null)}
                    title={`${currentContract?.name} ( ${formatUAHStandard(
                        currentContract?.context?.placePrice ?? 0,
                    )})`}
                    description={currentContract?.description ?? ""}
                    files={currentContract?.files}
                />
            )}
        </>
    );
};
